import styled from "styled-components";

import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { Box, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import type { RegionalDomainData } from "app/pages/industry-analysis/players/market-shares/types";
import { getAbsoluteFormat } from "app/pages/market-research/features/search-trends-page/demand-over-time/utils/utils";

const HintHeader = styled.div`
    font-weight: 700;
`;

const HintList = styled.ul`
    ${setFontVariant("tooltip", colorsPalettes.carbon[0])};
    list-style-type: disc;
    margin: 0 0 0 16px;
`;

const HintListItem = styled.li`
    margin-left: 4px;
`;

interface IRegionalsLegendItemHintParams {
    parentDomainName: string;
    domains: RegionalDomainData[];
    onViewCountryDomainsClick?: (parentDomainName: string, domains: RegionalDomainData[]) => void;
}

const MAX_HINT_DOMAINS = 5;

export const RegionalsLegendItemHint = ({
    parentDomainName,
    domains,
    onViewCountryDomainsClick,
}: IRegionalsLegendItemHintParams) => {
    const sortedDomains = domains.sort((a, b) => b.AvgMonthVisits - a.AvgMonthVisits).slice(0, MAX_HINT_DOMAINS);
    const otherCount = domains.length - MAX_HINT_DOMAINS;
    const otherText = translate(
        otherCount === 1 ? "market.players.regionals.others.singular" : "market.players.regionals.others",
        {
            countOfOtherDomains: otherCount,
        },
    );
    // %domainsCount% country domains
    const otherVolume = getAbsoluteFormat(
        domains.slice(MAX_HINT_DOMAINS).reduce((acc, curr) => acc + curr.AvgMonthVisits, 0),
    );
    return (
        <Box width="200px">
            <HintHeader>
                {translate("website.analysis.geography.regional.legend.tooltip.header", {
                    domainsCount: domains?.length,
                })}
            </HintHeader>
            <HintList>
                {sortedDomains.map((row, index) => (
                    <HintListItem key={`${row.Domain}_${index}`}>
                        {row.Domain} - {getAbsoluteFormat(row.AvgMonthVisits)}
                    </HintListItem>
                ))}
                {domains.length > MAX_HINT_DOMAINS ? (
                    <HintListItem key={`OTHER_${MAX_HINT_DOMAINS + 1}`}>
                        {otherText} - {otherVolume}
                    </HintListItem>
                ) : null}
            </HintList>
            <Text
                cursor="pointer"
                fontVariant="tooltip"
                color={colorsPalettes.blue[300]}
                onClick={() => onViewCountryDomainsClick(parentDomainName, domains)}
            >
                {translate("website.analysis.geography.regional.legend.tooltip.button.view")}
            </Text>
        </Box>
    );
};
